import { useState, useEffect } from 'react'
import { Loader2, CalendarClock, Percent, Clock, DollarSign, AlertTriangle } from 'lucide-react'
import { api } from '../../api/client'

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0)
const fmtD = (d: string) => d ? new Date(d + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

interface CreditCard {
  account_id: string; name: string; mask?: string; institution?: string;
  current_balance: number; credit_limit?: number; available_credit?: number;
  purchase_apr?: number; apr_range_low?: number; apr_range_high?: number; apr_source?: string;
  all_aprs?: { apr_percentage: number; apr_type: string }[];
  next_due_date?: string; minimum_payment?: number;
  monthly_interest?: number; payoff_months?: number; total_interest_at_minimum?: number;
  last_payment_amount?: number; last_payment_date?: string;
  last_statement_balance?: number; last_statement_date?: string;
  is_overdue?: boolean; source?: string;
}
interface CreditResp {
  cards: CreditCard[]; total_balance: number; total_limit: number;
  utilization: number; total_monthly_interest: number; total_annual_interest: number;
}

function daysUntilDue(dueDate: string | undefined): number | null {
  if (!dueDate) return null
  try {
    const due = new Date(dueDate + 'T00:00')
    const now = new Date(); now.setHours(0, 0, 0, 0)
    return Math.ceil((due.getTime() - now.getTime()) / 86400000)
  } catch { return null }
}

export function SmartPayPage() {
  const [data, setData] = useState<CreditResp | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get<CreditResp>('/api/credit-cards')
      .then(setData)
      .catch((e: Error) => { console.error(e); setError(e.message || 'Failed to load credit cards') })
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>

  const cards = data?.cards ?? []
  const utilization = data?.utilization ?? 0

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">Credit Cards</h2>

      {error && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-400">{error}</div>
      )}

      {/* Summary */}
      {data && cards.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-slate-400">Total Balance</p>
              <p className="text-lg sm:text-2xl font-bold text-rose-400">{fmt(data.total_balance ?? 0)}</p>
            </div>
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-slate-400">Total Limit</p>
              <p className="text-lg sm:text-2xl font-bold text-white">{fmt(data.total_limit ?? 0)}</p>
            </div>
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-slate-400">Utilization</p>
              <p className={`text-lg sm:text-2xl font-bold ${utilization > 30 ? 'text-rose-400' : 'text-emerald-400'}`}>{utilization.toFixed(1)}%</p>
            </div>
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-slate-400">Monthly Interest</p>
              <p className="text-lg sm:text-2xl font-bold text-amber-400">{fmt(data.total_monthly_interest ?? 0)}</p>
            </div>
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-3 sm:p-4 col-span-2 sm:col-span-1">
              <p className="text-xs sm:text-sm text-slate-400">Annual Interest</p>
              <p className="text-lg sm:text-2xl font-bold text-rose-400">{fmt(data.total_annual_interest ?? 0)}</p>
            </div>
          </div>
        </>
      )}

      {/* Credit Cards */}
      {cards.length > 0 ? (
        <div className="space-y-4">
          {cards.map((c, i) => {
            const bal = c.current_balance ?? 0
            const lim = c.credit_limit ?? 0
            const util = lim > 0 ? (bal / lim * 100) : 0
            const apr = c.purchase_apr
            const days = daysUntilDue(c.next_due_date)
            const overdue = days !== null && days < 0
            const dueSoon = days !== null && days >= 0 && days <= 7
            return (
              <div key={c.account_id || i} className={`rounded-xl border p-4 sm:p-5 ${overdue ? 'border-rose-500/50 bg-rose-500/5' : 'border-slate-700/50 bg-slate-800/80'}`}>
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-slate-200">{c.name}{c.mask ? ` ····${c.mask}` : ''}</p>
                    {c.institution && <p className="text-xs text-slate-500">{c.institution}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-rose-400">{fmt(bal)}</p>
                    {c.available_credit != null && <p className="text-xs text-slate-500">{fmt(c.available_credit)} available</p>}
                  </div>
                </div>

                {/* Utilization Bar */}
                {lim > 0 && (
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                      <span>{fmt(bal)} / {fmt(lim)}</span>
                      <span className={util > 30 ? 'text-rose-400 font-medium' : 'text-emerald-400'}>{util.toFixed(0)}% used</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-700 overflow-hidden">
                      <div className={`h-full rounded-full ${util > 50 ? 'bg-rose-500' : util > 30 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(util, 100)}%` }} />
                    </div>
                  </div>
                )}

                {/* Key Metrics Row */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 mb-3">
                  {/* Due Date */}
                  <div className="rounded-lg bg-slate-900/50 p-2.5">
                    <div className="flex items-center gap-1 text-xs text-slate-500 mb-0.5">
                      <CalendarClock className="h-3 w-3" /> Due Date
                    </div>
                    {c.next_due_date ? (
                      <>
                        <p className={`text-sm font-semibold ${overdue ? 'text-rose-400' : dueSoon ? 'text-amber-400' : 'text-slate-200'}`}>
                          {fmtD(c.next_due_date)}
                        </p>
                        {overdue && <p className="text-xs text-rose-400 font-medium">{Math.abs(days!)} days overdue</p>}
                        {dueSoon && !overdue && <p className="text-xs text-amber-400">{days} day{days !== 1 ? 's' : ''} left</p>}
                      </>
                    ) : (
                      <p className="text-sm text-slate-600">Not set</p>
                    )}
                  </div>

                  {/* APR */}
                  <div className="rounded-lg bg-slate-900/50 p-2.5">
                    <div className="flex items-center gap-1 text-xs text-slate-500 mb-0.5">
                      <Percent className="h-3 w-3" /> APR
                    </div>
                    {apr != null ? (
                      <>
                        <p className="text-sm font-semibold text-slate-200">{apr.toFixed(2)}%</p>
                        {c.apr_source === 'ai_estimate' && <p className="text-xs text-slate-600">AI estimate</p>}
                        {c.apr_range_low != null && c.apr_range_high != null && (
                          <p className="text-xs text-slate-600">{c.apr_range_low}–{c.apr_range_high}%</p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-slate-600">Unknown</p>
                    )}
                  </div>

                  {/* Minimum Payment */}
                  <div className="rounded-lg bg-slate-900/50 p-2.5">
                    <div className="flex items-center gap-1 text-xs text-slate-500 mb-0.5">
                      <DollarSign className="h-3 w-3" /> Min Payment
                    </div>
                    {c.minimum_payment != null ? (
                      <p className="text-sm font-semibold text-slate-200">{fmt(c.minimum_payment)}</p>
                    ) : (
                      <p className="text-sm text-slate-600">Not set</p>
                    )}
                  </div>

                  {/* Monthly Interest */}
                  <div className="rounded-lg bg-slate-900/50 p-2.5">
                    <div className="flex items-center gap-1 text-xs text-slate-500 mb-0.5">
                      <AlertTriangle className="h-3 w-3" /> Monthly Interest
                    </div>
                    {c.monthly_interest != null ? (
                      <p className="text-sm font-semibold text-amber-400">{fmt(c.monthly_interest)}</p>
                    ) : (
                      <p className="text-sm text-slate-600">—</p>
                    )}
                  </div>
                </div>

                {/* Payoff Analysis */}
                {(c.payoff_months != null && c.payoff_months > 0) && (
                  <div className="rounded-lg border border-slate-700/30 bg-slate-900/30 p-3">
                    <p className="text-xs font-medium text-slate-400 mb-1.5">
                      <Clock className="inline h-3 w-3 mr-1" />Payoff Analysis (at minimum payment)
                    </p>
                    <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
                      <div>
                        <p className="text-xs sm:text-sm text-slate-500">Time to pay off</p>
                        <p className="text-sm font-semibold text-white">
                          {c.payoff_months < 12
                            ? `${c.payoff_months} month${c.payoff_months !== 1 ? 's' : ''}`
                            : `${Math.floor(c.payoff_months / 12)}yr ${c.payoff_months % 12}mo`}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-slate-500">Total interest paid</p>
                        <p className="text-sm font-semibold text-rose-400">{fmt(c.total_interest_at_minimum ?? 0)}</p>
                      </div>
                    </div>
                  </div>
                )}
                {c.payoff_months === null && bal > 0 && apr != null && (
                  <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3">
                    <p className="text-xs text-rose-400 font-medium">
                      <AlertTriangle className="inline h-3 w-3 mr-1" />
                      Minimum payment doesn't cover interest — balance will grow!
                    </p>
                  </div>
                )}

                {/* Statement Info */}
                {(c.last_payment_amount != null || c.last_statement_balance != null) && (
                  <div className="mt-3 flex flex-col gap-1 text-xs text-slate-500 sm:flex-row sm:gap-4">
                    {c.last_payment_amount != null && (
                      <span>Last payment: {fmt(c.last_payment_amount)}{c.last_payment_date ? ` on ${fmtD(c.last_payment_date)}` : ''}</span>
                    )}
                    {c.last_statement_balance != null && (
                      <span>Statement bal: {fmt(c.last_statement_balance)}{c.last_statement_date ? ` (${fmtD(c.last_statement_date)})` : ''}</span>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : !error && (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-12 text-center">
          <p className="text-lg font-semibold text-slate-200">No credit cards found</p>
          <p className="mt-2 text-sm text-slate-500">Link your bank accounts and sync to see credit card details.</p>
        </div>
      )}
    </div>
  )
}
